import React, { useMemo, useState } from 'react';
import {
  Box, IconButton, Menu, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import { colors, typography } from '../../../../styles/theme';
import ActionMenuItem, {
  ACTION_MENU_LIST_PROPS,
  ACTION_MENU_PAPER_PROPS,
} from '../../ActionMenuItem';
import { SECTION_BAR_ICON_BUTTON_SX } from './formBlockUi';
import { isHighlightedUnit, UNIT_HIGHLIGHT_SX, useScrollIntoViewWhen } from './unitHighlight';
import {
  formatFiscalArs,
  formatOfficialBuiltSurface,
  getOfficialUnitNumber,
  matchesOfficialUnitSearch,
} from '../../../utils/lotFunctionalUnits';
import { formatUserLotCommercial } from '../../../utils/userLotFormUtils';
import CommercialStatusDot from '../../../../Leads/components/CommercialStatusDot';
import {
  officialCardClickableSx,
  officialCardSx,
  officialListScrollSx,
  officialMetaSx,
  officialNumberTextSx,
  officialNumberTileSx,
  officialSearchFieldSx,
  officialTitleSx,
} from './OfficialUnitsList.styles';

const SEARCH_THRESHOLD = 8;

const overlayTitle = (overlay) => {
  if (!overlay) return null;
  return overlay.title || overlay.unit_label || null;
};

const OfficialUnitCard = ({
  unit,
  overlay,
  canEdit,
  onCargarInfo,
  onSelectOverlay,
  onEditOverlay,
  onRemoveOverlay,
  removeOverlayRequiresCanDelete = false,
  highlighted = false,
}) => {
  const cardRef = useScrollIntoViewWhen(highlighted);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const unitNumber = getOfficialUnitNumber(unit) || '—';
  const userLotId = unit.user_lot_id;
  const annotated = Boolean(userLotId);
  const surface = formatOfficialBuiltSurface(unit.built_surface_m2);
  const fiscal = formatFiscalArs(unit.fiscal_total_ars);
  const title = annotated
    ? (overlayTitle(overlay) || `UF ${unitNumber}`)
    : `UF ${unitNumber}`;
  const commercial = annotated ? formatUserLotCommercial(overlay) : null;
  const metaParts = [
    unit.tax_account,
    surface,
    !annotated ? fiscal : null,
    commercial,
  ].filter(Boolean);

  const openOverlay = () => {
    if (annotated && userLotId) onSelectOverlay?.(userLotId);
  };

  return (
    <Box
      ref={cardRef}
      data-testid={highlighted ? 'unit-highlighted' : undefined}
      onClick={annotated ? openOverlay : undefined}
      sx={[
        annotated ? officialCardClickableSx : officialCardSx,
        highlighted ? UNIT_HIGHLIGHT_SX : null,
      ]}
    >
      <Box sx={officialNumberTileSx} aria-hidden>
        <Typography sx={officialNumberTextSx}>{unitNumber}</Typography>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Typography sx={officialTitleSx}>{title}</Typography>
          {/* Solo si la fila trae `commercial_summary` (dueño / editor; nunca share). */}
          {annotated ? <CommercialStatusDot summary={overlay?.commercial_summary || null} /> : null}
        </Box>
        <Typography sx={officialMetaSx}>
          {metaParts.join(' · ') || 'Sin datos catastrales'}
        </Typography>
      </Box>

      {canEdit && !annotated && (
        <Tooltip title="Agregar" placement="top" arrow>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onCargarInfo?.(unit);
            }}
            aria-label={`Agregar información a UF ${unitNumber}`}
            sx={SECTION_BAR_ICON_BUTTON_SX}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      )}

      {annotated && (
        <>
          {canEdit && (
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                setMenuAnchor(event.currentTarget);
              }}
              aria-label={`Acciones de UF ${unitNumber}`}
              sx={{
                ...SECTION_BAR_ICON_BUTTON_SX,
                border: 'none',
                color: colors.textSecondary,
                '& .MuiSvgIcon-root': { fontSize: 18, color: colors.textSecondary },
              }}
            >
              <MoreVertIcon />
            </IconButton>
          )}
          <ChevronRightIcon
            className="official-unit-chevron"
            sx={{
              fontSize: 20,
              color: colors.textSecondary,
              flexShrink: 0,
            }}
          />
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            onClick={(event) => event.stopPropagation()}
            PaperProps={ACTION_MENU_PAPER_PROPS}
            MenuListProps={ACTION_MENU_LIST_PROPS}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <ActionMenuItem
              icon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
              label="Editar"
              onClick={() => {
                setMenuAnchor(null);
                onEditOverlay?.(userLotId);
              }}
            />
            {/* Espacio ajeno: solo si la API dice `can_delete` (UF que creó el invitado). */}
            {onRemoveOverlay && (!removeOverlayRequiresCanDelete || overlay?.can_delete === true) ? (
              <ActionMenuItem
                icon={<DeleteOutlineIcon sx={{ fontSize: 18 }} />}
                label="Eliminar"
                onClick={() => {
                  setMenuAnchor(null);
                  onRemoveOverlay(userLotId);
                }}
              />
            ) : null}
          </Menu>
        </>
      )}
    </Box>
  );
};

/**
 * Lista de UF oficiales (catastro). No mezcla las propias extra: esas van en
 * `UnitListView` debajo, vía `UnitsTabList`.
 */
const OfficialUnitsList = ({
  units = [],
  overlayByUserLotId,
  canEdit = false,
  onCargarInfo,
  onSelectOverlay,
  onEditOverlay,
  onRemoveOverlay,
  removeOverlayRequiresCanDelete = false,
  highlightUnitId = null,
}) => {
  const [query, setQuery] = useState('');
  const overlayMap = overlayByUserLotId instanceof Map
    ? overlayByUserLotId
    : new Map();

  const filtered = useMemo(
    () => units.filter((unit) => matchesOfficialUnitSearch(unit, query)),
    [units, query],
  );

  const showSearch = units.length >= SEARCH_THRESHOLD;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {showSearch && (
        <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${colors.border}` }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Buscar por unidad funcional o cuenta"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ fontSize: 18, color: colors.textSecondary, mr: 1 }} />
              ),
            }}
            sx={officialSearchFieldSx}
          />
        </Box>
      )}

      <Box sx={officialListScrollSx}>
        {filtered.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: '12px',
                color: colors.textSecondary,
                fontFamily: typography.fontFamily.primary,
              }}
            >
              Ninguna unidad coincide con la búsqueda.
            </Typography>
          </Box>
        ) : (
          filtered.map((unit) => (
            <OfficialUnitCard
              key={unit.id || unit.unit_number}
              unit={unit}
              overlay={unit.user_lot_id ? overlayMap.get(String(unit.user_lot_id)) : null}
              canEdit={canEdit}
              onCargarInfo={onCargarInfo}
              onSelectOverlay={onSelectOverlay}
              onEditOverlay={onEditOverlay}
              onRemoveOverlay={onRemoveOverlay}
              removeOverlayRequiresCanDelete={removeOverlayRequiresCanDelete}
              highlighted={isHighlightedUnit(highlightUnitId, unit.user_lot_id)}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default OfficialUnitsList;
